import React, {useState, useEffect, useRef} from "react";
import { useParams, useNavigate } from "react-router-dom";
import Menu from "../../../../components/layout/menu";
import { getNewsDetail, getNews } from "../../../../client/news";
import NewsCard from "../../../../components/card/newsCard";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import bannerDefault from "../../../../assets/images/news/banner-default.png"

const CustomPrevArrow = ({ onClick, currentSlide }) => (
  <button 
    onClick={onClick}
    className={`absolute z-10 transform rotate-180 -translate-y-1/2 cursor-pointer -left-3 sm:-left-2 md:-left-3 lg:-left-4 xl:-left-6 top-1/2 ${
      currentSlide === 0 ? 'opacity-0 pointer-events-none' : 'opacity-100'
    } transition-opacity duration-300`}
  >
    <svg className="w-4 lg:w-5 xl:w-6" viewBox="0 0 28 31" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M27 13.7679C28.3333 14.5377 28.3333 16.4622 27 17.232L3.75 30.6554C2.41667 31.4252 0.749998 30.463 0.749999 28.9234L0.75 2.0766C0.75 0.537005 2.41667 -0.425246 3.75 0.344555L27 13.7679Z" fill="#007696"/>
    </svg>
  </button>
);

const CustomNextArrow = ({ onClick, currentSlide, slideCount, slidesToShow }) => {
  const isLastSlide = currentSlide >= slideCount - slidesToShow;
  
  return (
    <button 
      onClick={onClick}
      className={`absolute -right-3 sm:-right-2 md:-right-3 lg:-right-4 xl:-right-6 top-1/2 transform -translate-y-1/2 z-10 cursor-pointer ${
        isLastSlide ? 'opacity-30 pointer-events-none' : 'opacity-100'
      } transition-opacity duration-300`}
    >
      <svg className="w-4 lg:w-5 xl:w-6" viewBox="0 0 28 31" fill="none">
        <path d="M27 13.7679C28.3333 14.5377 28.3333 16.4622 27 17.232L3.75 30.6554C2.41667 31.4252 0.749998 30.463 0.749999 28.9234L0.75 2.0766C0.75 0.537005 2.41667 -0.425246 3.75 0.344555L27 13.7679Z" fill="#007696"/>
      </svg>
    </button>
  );
};

export default function NewsDetailProvider() {
  const { id } = useParams(); 
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newsData, setNewsData] = useState(null);

  const [recommendedNews, setRecommendedNews] = useState([]);
  const [isRecommendedLoading, setIsRecommendedLoading] = useState(false);

  const [currentNewsIndex, setCurrentNewsIndex] = useState(0);
  const [maxVisibleNews, setMaxVisibleNews] = useState(3);
  const newsCarouselRef = useRef(null);
  const [isDraggingNews, setIsDraggingNews] = useState(false);
  const [startXNews, setStartXNews] = useState(0);
  const [scrollLeftNews, setScrollLeftNews] = useState(0);

  const [gallerySlidesToShow, setGallerySlidesToShow] = useState(3);
  const [currentGallerySlide, setCurrentGallerySlide] = useState(0);
  const gallerySliderRef = useRef(null);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { day: 'numeric', month: 'short', year: 'numeric' };
    return date.toLocaleDateString('es-ES', options);
  };

  const fetchRecommendedNews = async () => {
    try {
      setIsRecommendedLoading(true);
      const response = await getNews("", "", "", parseInt(id));
      if (response.data && response.data.news) {
        const visibleNews = response.data.news
          .filter(newsItem => newsItem.visible === true)
        setRecommendedNews(visibleNews);
      }
      setIsRecommendedLoading(false);
    } catch (err) {
      console.error("Error fetching recommended news:", err);
      setIsRecommendedLoading(false);
    }
  };

useEffect(() => {
  const fetchNewsDetail = async () => {
    try {
      setIsLoading(true);
      const response = await getNewsDetail(id);
      if (response.data && response.data.news) {
        setNewsData(response.data.news);
        await fetchRecommendedNews();
      }
    } catch (err) {
      setError("Error al cargar la noticia");
      console.error("Error fetching news detail:", err);
    } finally {
      setIsLoading(false); 
    }
  };

  if (id) {
    fetchNewsDetail();
  }
}, [id]);

  const getGallerySlickSettings = () => ({
    dots: false,
    infinite: false,
    speed: 300,
    slidesToShow: gallerySlidesToShow,
    slidesToScroll: gallerySlidesToShow,
    beforeChange: (oldIndex, newIndex) => setCurrentGallerySlide(newIndex),
    prevArrow: <CustomPrevArrow currentSlide={currentGallerySlide} />,
    nextArrow: <CustomNextArrow 
      currentSlide={currentGallerySlide} 
      slideCount={newsData?.newsImages?.length || 0}
      slidesToShow={gallerySlidesToShow}
    />,
    responsive: [
      {
        breakpoint: 1570,
        settings: {
          slidesToShow: Math.min(gallerySlidesToShow, 2),
          slidesToScroll: Math.min(gallerySlidesToShow, 2),
        }
      },
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: Math.min(gallerySlidesToShow, 2),
          slidesToScroll: Math.min(gallerySlidesToShow, 2),
        }
      },
      {
        breakpoint: 900,
        settings: {
          slidesToShow: Math.min(gallerySlidesToShow, 2),
          slidesToScroll: Math.min(gallerySlidesToShow, 2),
        }
      },
      {
        breakpoint: 837,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        }
      }
    ]
  });

  useEffect(() => {
    const calculateGallerySlidesToShow = () => {
      const screenWidth = window.innerWidth;
      
      let availableWidth = screenWidth;
      if (isMenuOpen) {
        if (screenWidth >= 1280) {
          availableWidth = screenWidth * 0.793;
        } else if (screenWidth >= 1024) {
          availableWidth = screenWidth * 0.763;
        }
      }
      
      if (availableWidth < 837) {
        setGallerySlidesToShow(1);
      } else if (availableWidth < 1255) {
        setGallerySlidesToShow(2);
      } else if (availableWidth < 1673) {
        setGallerySlidesToShow(3);
      } else if (availableWidth < 2091){
        setGallerySlidesToShow(4);
      } else {
        setGallerySlidesToShow(5);
      }
    };

    calculateGallerySlidesToShow();

    const handleResize = () => {
      calculateGallerySlidesToShow();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMenuOpen]);



useEffect(() => {
  const calculateVisibleNews = () => {
    const screenWidth = window.innerWidth;
    
    const adjustedWidth = isMenuOpen 
      ? screenWidth - (screenWidth >= 1280 ? screenWidth * 0.207 : screenWidth >= 1024 ? screenWidth * 0.237 : 0)
      : screenWidth;
    
    if (adjustedWidth < 642) {
      setMaxVisibleNews(1);
    } else if (adjustedWidth < 900) {
      setMaxVisibleNews(2);
    } else if (adjustedWidth < 1200) {
      setMaxVisibleNews(3);
    } else {
      setMaxVisibleNews(4);
    }
  };

  calculateVisibleNews();

  const handleResize = () => {
    calculateVisibleNews();
  };

  window.addEventListener('resize', handleResize);
  return () => window.removeEventListener('resize', handleResize);
}, [isMenuOpen]);

const nextNewsSlide = () => {
  const totalItems = recommendedNews.length;
  if (currentNewsIndex < totalItems - maxVisibleNews) {
    setCurrentNewsIndex(prev => Math.min(prev + maxVisibleNews, totalItems - maxVisibleNews));
  }
};

const prevNewsSlide = () => {
  if (currentNewsIndex > 0) {
    setCurrentNewsIndex(prev => Math.max(prev - maxVisibleNews, 0));
  }
};

  const handleMouseDownNews = (e) => {
    setIsDraggingNews(true);
    setStartXNews(e.pageX - newsCarouselRef.current.offsetLeft);
    setScrollLeftNews(newsCarouselRef.current.scrollLeft);
  };

  const handleMouseLeaveNews = () => setIsDraggingNews(false);
  const handleMouseUpNews = () => setIsDraggingNews(false);

  const handleMouseMoveNews = (e) => {
    if (!isDraggingNews) return;
    e.preventDefault();
    const x = e.pageX - newsCarouselRef.current.offsetLeft;
    const walk = (x - startXNews) * 2;
    newsCarouselRef.current.scrollLeft = scrollLeftNews - walk;
  };

  const handleTouchStartNews = (e) => {
    setIsDraggingNews(true);
    setStartXNews(e.touches[0].pageX - newsCarouselRef.current.offsetLeft);
    setScrollLeftNews(newsCarouselRef.current.scrollLeft);
  };

  const handleTouchMoveNews = (e) => {
    if (!isDraggingNews) return;
    const x = e.touches[0].pageX - newsCarouselRef.current.offsetLeft;
    const walk = (x - startXNews) * 2;
    newsCarouselRef.current.scrollLeft = scrollLeftNews - walk;
  };




  return (
    <div className="min-h-screen">
      <Menu isMenuOpen={isMenuOpen} toggleMenu={toggleMenu} className="transition-all duration-300" />
      <div className={`flex-1 transition-all duration-300 pt-[67px]  bg-[#F7F8F9] w-full z-50 min-h-screen ${isMenuOpen ? "xl:pl-[20.7%] lg:pl-[23.7%] pl-0" : "pl-0"}`}>
        <div className="sm:pt-8 pt-6 pb-10 px-[5%] sm:px-[3%]">


          {isLoading ? (
            <div className="flex items-center justify-center min-h-[80vh]">
              <p className="font-itcmedium text-gray">Cargando noticia...</p>
            </div>
          ) : error || !newsData ? (
            <div className="flex items-center justify-center min-h-[80vh]">
              <p className="text-red-500 font-itcmedium">{error || "Noticia no encontrada"}</p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
                <p className="text-lg items-center font-itcbook flex gap-1 text-[#24325A]">
                    <button 
                        onClick={() => navigate('/conocenos')} 
                        className="hover:underline"
                    >
                        Noticias
                    </button>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4.83008 2.55301L9.38365 7.10658L4.83008 11.6602" stroke="#EF6224" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    {newsData.title}
                </p>
                { newsData.image_blog ? (
                  <div className="relative w-full overflow-hidden rounded-2xl" style={{ aspectRatio: '1078/482', backgroundColor: '#f3f4f6' }}>
                    <img src={newsData.image_blog }
                        className="object-contain w-full h-full"
                     />
                  </div>
                ) : (
                  <div className="relative w-full overflow-hidden rounded-2xl" style={{ aspectRatio: '1078/482' }}>
                    <img src={bannerDefault} 
                      className="object-cover w-full h-full"
                    />
                  </div>
                )

                }
                <div className="flex items-center justify-between gap-2 ">
                    <div className="pt-1 px-2 flex items-center bg-[#BBBFCC] rounded-lg  ">
                        <p className="text-sm font-anekmedium">
                            {formatDate(newsData.created_At)}
                        </p>  
                    </div>
                    <div className="pt-1 px-2 flex items-center bg-[#EFB86A] rounded-lg ">
                       <p className="text-sm text-white font-anekmedium">Noticias</p>
                    </div>
                </div>
                <h1 className="text-blue font-itcdemi text-[32px]">
                    {newsData.title}
                </h1>

                <style>
                  {`
                    .news-content h1 {
                      font-size: 2em !important;
                    }
                    .news-content h2 {
                      font-size: 1.5em !important;
                    }
                    .news-content h3 {
                      font-size: 1.17em !important;
                    }
                  `}
                </style>
                <div 
                  className="text-[#323F49] font-itcbook news-content"
                  dangerouslySetInnerHTML={{ __html: newsData.extract || '' }}
                />
                <h1 className="text-blue font-itcdemi text-[32px]">
                    {newsData.subtitle}
                </h1>
                <div 
                  className="text-[#323F49] font-itcbook news-content"
                  dangerouslySetInnerHTML={{ __html: newsData.content || '' }}
                />
                
                {newsData.newsImages && newsData.newsImages.length > 0 && (
                  <>
                    <h2 className="text-[#24325A] text-[32px] font-itcdemi">
                        Galería de fotos
                    </h2>
                    <div className="relative">
                      <Slider 
                        ref={gallerySliderRef}
                        {...getGallerySlickSettings()}
                        className="gallery-slider"
                      >
                        {newsData.newsImages.slice(0, 5).map((image, index) => (
                          <div key={index} className="px-2">
                            <div className="flex flex-col items-center justify-center rounded-2xl">
                              <img 
                                src={image.url} 
                                alt={`Imagen ${index + 1}`} 
                                className="object-cover  w-[378px] h-[269px] rounded-2xl"
                              />
                            </div>
                          </div>
                        ))}
                      </Slider>
                    </div>
                  </>
                )}
                
                <h2 className="text-[#24325A] text-[32px] font-itcdemi">
                    Recomendaciones
                </h2>

                {isRecommendedLoading ? (
                  <div className="flex items-center justify-center h-40">
                    <p>Cargando recomendaciones...</p>
                  </div>
                ) : recommendedNews.length > 0 ? (
                  <div className="relative">
                    {/* Flecha izquierda */}
                    {currentNewsIndex > 0 && (
                      <button onClick={prevNewsSlide} className="absolute z-10 transform rotate-180 -translate-y-1/2 cursor-pointer -left-1 sm:-left-2 md:-left-3 lg:-left-4 xl:-left-6 top-1/2">
                        <svg className="w-4 lg:w-5 xl:w-6" viewBox="0 0 28 31" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M27 13.7679C28.3333 14.5377 28.3333 16.4622 27 17.232L3.75 30.6554C2.41667 31.4252 0.749998 30.463 0.749999 28.9234L0.75 2.0766C0.75 0.537005 2.41667 -0.425246 3.75 0.344555L27 13.7679Z" fill="#007696"/>
                        </svg>
                      </button>
                    )}

                    {/* Flecha derecha */}
                    {currentNewsIndex < Math.max(0, (recommendedNews.length) - maxVisibleNews) && (
                      <button 
                        onClick={nextNewsSlide} 
                        disabled={currentNewsIndex >= Math.max(0, (recommendedNews.length) - maxVisibleNews)}
                        className={`absolute -right-3 sm:-right-4 md:-right-5 lg:-right-6 xl:-right-7 top-1/2 transform -translate-y-1/2 z-10 cursor-pointer
                          ${currentNewsIndex >= Math.max(0, (recommendedNews.length) - maxVisibleNews) ? 'opacity-30' : ''}`}
                      >
                        <svg className="w-4 lg:w-5 xl:w-6" viewBox="0 0 28 31" fill="none">
                          <path d="M27 13.7679C28.3333 14.5377 28.3333 16.4622 27 17.232L3.75 30.6554C2.41667 31.4252 0.749998 30.463 0.749999 28.9234L0.75 2.0766C0.75 0.537005 2.41667 -0.425246 3.75 0.344555L27 13.7679Z" fill="#007696"/>
                        </svg>
                      </button>
                    )}

                    <div 
                      ref={newsCarouselRef}
                      className="flex-1 overflow-hidden"
                      onMouseDown={handleMouseDownNews}
                      onMouseLeave={handleMouseLeaveNews}
                      onMouseUp={handleMouseUpNews}
                      onMouseMove={handleMouseMoveNews}
                      onTouchStart={handleTouchStartNews}
                      onTouchMove={handleTouchMoveNews}
                      onTouchEnd={handleMouseUpNews}
                    >
                      <div 
                        style={{ 
                          transform: `translateX(-${currentNewsIndex * (267 + 32)}px)`,
                          transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}
                        className="grid grid-flow-col auto-cols-[267px] pl-4 py-2 gap-8 transition-transform duration-300"
                      >
                        {recommendedNews.map((newsItem) => (
                          <NewsCard
                            key={newsItem.id}
                            id={newsItem.id}
                            title={newsItem.title}
                            description={newsItem.advance || newsItem.extract}
                            image={newsItem.image_list || newsItem.image_blog}
                            date={formatDate(newsItem.created_At)}
                            initialVisible={newsItem.visible}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500">No hay noticias recomendadas disponibles</p>
                )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}